/*
 * Confidential and Proprietary.
 * Do not distribute without 1-800-Flowers.com, Inc. consent.
 * Copyright 1-800-Flowers.com, Inc. 2019. All rights reserved.
 */

import { useSelector } from 'react-redux';

import useExperience from './useExperience';
import { getOfferGatewayData, getOfferInboxData } from '../../../state/ducks/App/App-Selectors';
import { getProfileInfo, getUserEmailId } from '../../../state/ducks/Member/ducks/Common/Common-Selectors';

/**
 * Wrapper around useExperience that appends Offer Gateway-specific userAttributes
 * (OG opt-in state, unlock state, SMS flags, account cookie) without coupling
 * those concerns into the shared useExperience hook.
 *
 * Accepts the same params as useExperience, plus:
 * @param {boolean} isSessionHasAccount - when true, appends hasAccountCookie='true' userAttribute
 */
const useOfferGatewayExperience = ({
    interactionName,
    targeting = {},
    type = 'content',
    apolloOptions = {},
    resolveIdentity = false,
    queryName = 'PersonalizedOGExperience',
    featureFlag = true,
    isSessionHasAccount = false,
}) => {
    const offerGateway = useSelector(getOfferGatewayData);
    const offerInbox = useSelector(getOfferInboxData);
    const userInfo = useSelector(getProfileInfo);
    const emailId = useSelector(getUserEmailId);

    const userIdentity = {};
    if (offerGateway?.email || emailId) {
        userIdentity.emailId = offerGateway?.email || emailId;
    } else if (offerGateway?.phone) {
        userIdentity.phone = offerGateway?.phone;
    }

    let sessionOGOptin = `${offerGateway?.offerGatewayOptin || offerInbox?.offerGatewayOptin}`;
    let sessionOGLoggedIn = 'false';
    let sessionOGAccountCreation = 'false';

    if (userInfo?.offerGatewayOptin) {
        sessionOGOptin = `${userInfo.offerGatewayOptin === 'true'}`;
        sessionOGLoggedIn = `${userInfo?.loginsCount > 1}`;
        sessionOGAccountCreation = `${userInfo?.loginsCount === 1}`;
    }

    const ogUserAttributes = [
        { name: 'sessionOGOptIn', value: sessionOGOptin },
        { name: 'sessionOGLoggedIn', value: sessionOGLoggedIn },
        { name: 'sessionOGAccountCreation', value: sessionOGAccountCreation },
        { name: 'sessionOGOfferUnlock', value: `${offerGateway?.offerUnlocked}` },
        { name: 'sessionOGOptInSMS', value: `${offerGateway?.offerGatewayOptinSMS}` },
        { name: 'sessionOGOfferUnlockSMS', value: `${offerGateway?.offerUnlockedSMS}` },
    ];

    if (isSessionHasAccount) {
        ogUserAttributes.push({ name: 'hasAccountCookie', value: 'true' });
    }

    return useExperience({
        interactionName,
        targeting,
        userAttributes: ogUserAttributes,
        type,
        apolloOptions,
        resolveIdentity,
        queryName,
        featureFlag,
        userInfo: userIdentity,
    });
};

export default useOfferGatewayExperience;
